import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../hook/services";
import { toast } from "react-toastify";
import {
  Gear,
  Phone,
  ChatCircleText,
  Microphone,
  Lightning,
  Tag,
  Info,
} from "@phosphor-icons/react";
import LoadingScreen from "../../../components/LoadingScreen";

const DEFAULT_TEXT_BACK_MESSAGE =
  "We're sorry we missed your call, how can I help you?";
const TEXT_BACK_MAX_LENGTH = 320;

// Must match MISSED_CALL_STATUSES in backend/src/appTriggers/twilio/services.js
const MISSED_CALL_STATUSES = ["no-answer", "busy", "failed", "canceled"];

// Must match OPT_OUT_KEYWORDS in backend/src/_enums/enums.js — inbound SMS
// routes through the shared generic webhook (routes/webhook/services.js), which
// matches against that list.
const OPT_OUT_KEYWORDS = ["stop", "stopall", "unsubscribe", "cancel", "end", "quit"];

const CONTACT_SOURCE_TAGS = [
  { tag: "twilio-source-message-received", label: "Inbound text/image" },
  { tag: "twilio-source-call-missed", label: "Missed call" },
  { tag: "twilio-source-call-recording", label: "Call recording" },
  { tag: "twilio-source-manual-send", label: "Manually sent message" },
];

const SectionHeader = ({ icon, title, description }) => (
  <div className="d-flex align-items-center gap-3 mb-4">
    <div
      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{
        width: 40,
        height: 40,
        background: "linear-gradient(135deg, #3b82f6, #2563eb)",
        color: "white",
      }}
    >
      {icon}
    </div>
    <div>
      <div className="fw-semibold">{title}</div>
      {description && <div className="text-muted small">{description}</div>}
    </div>
  </div>
);

const ToggleRow = ({ icon, title, description, checked, onChange, id }) => (
  <div className="d-flex align-items-start justify-content-between gap-3 py-1">
    <div className="d-flex align-items-start gap-3">
      <div
        className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
        style={{ width: 34, height: 34, background: "#eff6ff", color: "#2563eb" }}
      >
        {icon}
      </div>
      <div>
        <label
          htmlFor={id}
          className="form-check-label fw-medium mb-0"
          style={{ cursor: "pointer" }}
        >
          {title}
        </label>
        {description && (
          <div className="text-muted small" style={{ maxWidth: 360 }}>
            {description}
          </div>
        )}
      </div>
    </div>
    <div className="form-check form-switch flex-shrink-0 mt-1">
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        id={id}
        checked={checked}
        onChange={onChange}
        style={{ width: "2.5rem", height: "1.4rem" }}
      />
    </div>
  </div>
);

const BadgeList = ({ caption, items }) => (
  <div className="mt-3 ms-5 ps-1">
    <div className="text-muted small mb-2">{caption}</div>
    <div className="d-flex flex-wrap gap-2">
      {items.map(({ key, label }) => (
        <span
          key={key}
          className="badge rounded-pill text-bg-light border fw-medium"
          style={{ padding: "0.4rem 0.75rem" }}
          title={label}
        >
          {key}
        </span>
      ))}
    </div>
  </div>
);

const Settings = () => {
  const { appId, locationId } = useParams();
  const AppService = useAppServices();
  const [appInstall, setAppInstall] = useState({});
  const [existingCredentials, setExistingCredentials] = useState({});
  const [formData, setFormData] = useState({
    dnd_on_opt_out_reply: true,
    log_calls_in_crm: true,
    missed_call_text_back_enabled: false,
    missed_call_text_back_message: DEFAULT_TEXT_BACK_MESSAGE,
    show_recording_in_crm: true,
    create_transcript_notes: true,
    create_summary_notes: true,
    enable_contact_source_tagging: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const installRes = await AppService.app_installs.filter({
        query: `appId=${appId}&locationId=${locationId}`,
      });
      if (installRes?.response) {
        setAppInstall(installRes.response.data);
        const creds = installRes.response.data?.credentials || {};
        setExistingCredentials(creds);
        setFormData({
          dnd_on_opt_out_reply: creds.dnd_on_opt_out_reply ?? true,
          log_calls_in_crm: creds.log_calls_in_crm ?? true,
          missed_call_text_back_enabled:
            creds.missed_call_text_back_enabled ?? false,
          missed_call_text_back_message:
            creds.missed_call_text_back_message || DEFAULT_TEXT_BACK_MESSAGE,
          show_recording_in_crm: creds.show_recording_in_crm ?? true,
          create_transcript_notes: creds.create_transcript_notes ?? true,
          create_summary_notes: creds.create_summary_notes ?? true,
          enable_contact_source_tagging:
            creds.enable_contact_source_tagging ?? false,
        });
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const toggle = (key) =>
    setFormData((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleMessageChange = (e) =>
    setFormData((prev) => ({
      ...prev,
      missed_call_text_back_message: e.target.value.slice(
        0,
        TEXT_BACK_MAX_LENGTH
      ),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      _id: appInstall?._id,
      // Merge with whatever Configuration already saved (acc_id, token,
      // from_number, poll watermarks) — credentials is one shared object.
      credentials: { ...existingCredentials, ...formData },
    };
    const { response } = await AppService.app_installs.update({ payload });
    setSaving(false);
    if (response) {
      setExistingCredentials((prev) => ({ ...prev, ...formData }));
      toast.success("Settings saved!");
    }
  };

  if (loading) {
    return <LoadingScreen loadingText={"Loading data..."} />;
  }

  const noFromNumber = !existingCredentials.from_number;

  return (
    <div className="py-5 bg-light min-vh-100">
      <div className="container-fluid px-4 px-xl-5">
        <div className="d-flex align-items-center gap-3 mb-4">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
            style={{
              width: 44,
              height: 44,
              background: "linear-gradient(135deg, #3b82f6, #2563eb)",
              color: "white",
            }}
          >
            <Gear size={20} weight="bold" />
          </div>
          <div>
            <h5 className="fw-semibold mb-0">Settings</h5>
            <div className="text-muted small">
              Compliance messaging and voice behavior for this number.
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row justify-content-center">
            <div className="col-xl-7 col-lg-8">
              <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body" style={{ padding: "2rem" }}>
                  {/* Opt-out handling */}
                  <SectionHeader
                    icon={<Lightning size={18} weight="bold" />}
                    title="Opt-Out Handling"
                    description="Automatically mark a contact as DND when they reply with an unsubscribe keyword"
                  />

                  <ToggleRow
                    id="dnd_on_opt_out_reply"
                    icon={<Lightning size={16} weight="bold" />}
                    title="Mark contact as DND on opt-out reply"
                    description="Checked automatically on every inbound text — no manual review needed."
                    checked={formData.dnd_on_opt_out_reply}
                    onChange={() => toggle("dnd_on_opt_out_reply")}
                  />

                  <BadgeList
                    caption="Recognized as an opt-out reply (case-insensitive, exact match):"
                    items={OPT_OUT_KEYWORDS.map((k) => ({ key: k }))}
                  />

                  <hr className="my-4" />

                  {/* Call logging */}
                  <SectionHeader
                    icon={<Phone size={18} weight="bold" />}
                    title="Call Logging"
                    description="Record call outcomes against the contact in the CRM conversation."
                  />

                  <ToggleRow
                    id="log_calls_in_crm"
                    icon={<Phone size={16} weight="bold" />}
                    title="Log calls in the CRM conversation"
                    description="One entry per finished call with its outcome and duration — final outcomes only, not every ring."
                    checked={formData.log_calls_in_crm}
                    onChange={() => toggle("log_calls_in_crm")}
                  />

                  <hr className="my-4" />

                  {/* Voice & Call Settings */}
                  <SectionHeader
                    icon={<ChatCircleText size={18} weight="bold" />}
                    title="Voice & Call Settings"
                    description="Manage what happens on missed calls."
                  />

                  <ToggleRow
                    id="missed_call_text_back_enabled"
                    icon={<ChatCircleText size={16} weight="bold" />}
                    title="Missed Call Text-Back"
                    description="Texts the caller from your default number when an incoming call goes unanswered. Sends a real SMS, so it is billed per message."
                    checked={formData.missed_call_text_back_enabled}
                    onChange={() => toggle("missed_call_text_back_enabled")}
                  />

                  {formData.missed_call_text_back_enabled && (
                    <div className="mt-3 ms-5 ps-1">
                      {noFromNumber && (
                        <div className="alert alert-warning py-2 px-3 small d-flex align-items-start gap-2 rounded-3">
                          <Info size={16} weight="bold" className="flex-shrink-0 mt-1" />
                          <span>
                            No default phone number is saved yet — pick one on
                            the Configuration page, or the text-back has nothing
                            to send from.
                          </span>
                        </div>
                      )}

                      <label
                        className="form-label small fw-medium"
                        htmlFor="missed_call_text_back_message"
                      >
                        Text-back message
                      </label>
                      <textarea
                        id="missed_call_text_back_message"
                        className="form-control rounded-3"
                        rows={3}
                        value={formData.missed_call_text_back_message}
                        onChange={handleMessageChange}
                      />
                      <small className="text-muted">
                        {formData.missed_call_text_back_message.length}/
                        {TEXT_BACK_MAX_LENGTH} characters
                      </small>

                      <div className="text-muted small mt-3 mb-2">
                        Sent when an incoming call ends with one of these
                        outcomes (outgoing calls never trigger it):
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {MISSED_CALL_STATUSES.map((status) => (
                          <span
                            key={status}
                            className="badge rounded-pill text-bg-light border fw-medium"
                            style={{ padding: "0.4rem 0.75rem" }}
                          >
                            {status}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <hr className="my-4" />

                  {/* Call Recording */}
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <h6 className="fw-semibold mb-0">Call Recording</h6>
                  </div>
                  <div className="text-muted small mb-3">
                    Check your local rules on one- vs. two-party consent before
                    enabling.
                  </div>

                  <div className="d-flex flex-column gap-3">
                    <ToggleRow
                      id="show_recording_in_crm"
                      icon={<Microphone size={16} weight="bold" />}
                      title="Show recording in the CRM conversation"
                      checked={formData.show_recording_in_crm}
                      onChange={() => toggle("show_recording_in_crm")}
                    />
                    <ToggleRow
                      id="create_transcript_notes"
                      icon={<Microphone size={16} weight="bold" />}
                      title="Create a note for the call transcript"
                      checked={formData.create_transcript_notes}
                      onChange={() => toggle("create_transcript_notes")}
                    />
                    <ToggleRow
                      id="create_summary_notes"
                      icon={<Microphone size={16} weight="bold" />}
                      title="Create a note for the call summary"
                      checked={formData.create_summary_notes}
                      onChange={() => toggle("create_summary_notes")}
                    />
                  </div>

                  <div className="mt-3 ms-5 ps-1">
                    <div className="alert alert-light border py-2 px-3 small d-flex align-items-start gap-2 rounded-3 mb-0">
                      <Info size={16} weight="bold" className="flex-shrink-0 mt-1" />
                      <span>
                        This app does not start recordings — it surfaces ones
                        your Twilio account already creates, however they were
                        set up.
                      </span>
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* Contact tagging */}
                  <SectionHeader
                    icon={<Tag size={18} weight="bold" />}
                    title="Contact Tagging"
                    description="Tag a contact with the event that created it — only on first creation, never on an already-existing contact."
                  />
                  <ToggleRow
                    id="enable_contact_source_tagging"
                    icon={<Tag size={16} weight="bold" />}
                    title="Tag new contacts with their source event"
                    checked={formData.enable_contact_source_tagging}
                    onChange={() => toggle("enable_contact_source_tagging")}
                  />
                  <BadgeList
                    caption="Possible tags:"
                    items={CONTACT_SOURCE_TAGS.map(({ tag, label }) => ({
                      key: tag,
                      label,
                    }))}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            <div className="col-xl-7 col-lg-8">
              <div className="d-flex justify-content-end mt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="btn btn-primary fw-semibold rounded-3 px-4"
                  style={{ padding: "0.7rem" }}
                >
                  {saving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving…
                    </>
                  ) : (
                    "Save Settings"
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
